import React from 'react';
import { motion } from 'framer-motion';
import { FaHome, FaMapMarkerAlt, FaMoneyBillWave, FaBed, FaUsers, FaBath, FaTag, FaInfoCircle, FaMapMarkedAlt, FaBuilding } from 'react-icons/fa';

const BasicInfoTab = ({ formData, handleChange }) => {
  // Custom handler for number fields to prevent negative values
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    
    // If the value is empty, allow it
    if (value === '') {
      handleChange({
        target: {
          name: name,
          value: value
        }
      });
      return;
    }
    
    // For non-empty values, validate that it's a non-negative number
    // Use parseFloat to handle decimal values correctly
    const numValue = parseFloat(value);
    
    // Only update if it's a valid non-negative number
    if (!isNaN(numValue) && numValue >= 0) {
      handleChange({
        target: {
          name: name,
          value: value // Keep the original string value to preserve decimal input
        }
      });
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaHome /> Property Title
          </label>
          <input 
            type="text" 
            className="form-control shadow-sm" 
            name="propertyTitle"
            value={formData.propertyTitle || ''}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaMapMarkerAlt /> Location
          </label>
          <input 
            type="text" 
            className="form-control shadow-sm" 
            name="location"
            value={formData.location || ''}
            onChange={handleChange}
            required
          />
        </div>
      </div>
      
      {/* New Row for Property Type */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaBuilding /> Property Type
          </label>
          <select
            className="form-select shadow-sm"
            name="propertyType"
            value={formData.propertyType || ''}
            onChange={handleChange}
          >
            <option value="">Select Type</option>
            <option value="Apartment">Apartment</option>
            <option value="Villa">Villa</option>
            <option value="Resort">Resort</option>
            <option value="Private Cottages Resort">Private Cottages Resort</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        {/* Show this field only when "Other" is selected */}
        {formData.propertyType === 'Other' && (
          <div className="col-md-6 mb-3">
            <label className="form-label d-flex align-items-center gap-2">
              <FaBuilding /> Custom Property Type
            </label>
            <input 
              type="text" 
              className="form-control shadow-sm" 
              name="customPropertyType"
              value={formData.customPropertyType || ''}
              onChange={handleChange}
              placeholder="Enter custom property type"
            />
          </div>
        )}
        
        <div className={`col-md-6 mb-3 ${formData.propertyType === 'Other' ? 'd-none' : ''}`}>
          <label className="form-label d-flex align-items-center gap-2">
            <FaMoneyBillWave /> Start Price (₹)
          </label>
          <div className="input-group">
            <span className="input-group-text">₹</span>
            <input 
              type="number" 
              className="form-control shadow-sm" 
              name="startPrice"
              value={formData.startPrice || ''}
              onChange={handleNumberChange}
              min="0"
              required
            />
          </div>
        </div>
      </div>
      
      <div className="row">
        {formData.propertyType === 'Other' && (
          <div className="col-md-3 col-12 mb-3">
            <label className="form-label d-flex align-items-center gap-2">
              <FaMoneyBillWave /> Start Price (₹)
            </label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input 
                type="number" 
                className="form-control shadow-sm" 
                name="startPrice"
                value={formData.startPrice || ''}
                onChange={handleNumberChange}
                min="0"
                required
              />
            </div>
          </div>
        )}
        {/* Group BHK Count and Max Occupancy in one row on mobile */}
        <div className="col-md-3 col-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaBed /> BHK Count
          </label>
          <input 
            type="number" 
            className="form-control shadow-sm" 
            name="bhkCount"
            value={formData.bhkCount || ''}
            onChange={handleNumberChange}
            min="0"
          />
        </div>
        <div className="col-md-3 col-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaUsers /> Max Occupancy
          </label>
          <input 
            type="number" 
            className="form-control shadow-sm" 
            name="maxOccupancy"
            value={formData.maxOccupancy || ''}
            onChange={handleNumberChange}
            min="0"
          />
        </div>
        <div className="col-md-3 col-12 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaTag /> Tag
          </label>
          <input 
            type="text" 
            className="form-control shadow-sm" 
            name="tag"
            value={formData.tag || ''}
            onChange={handleChange}
            placeholder="e.g. New, Featured, Popular"
          />
        </div>
      </div>
      
      <div className="row">
        {/* Group Beds and Baths in one row on mobile */}
        <div className="col-md-4 col-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaBed /> Beds
          </label>
          <input 
            type="number" 
            className="form-control shadow-sm" 
            name="beds"
            value={formData.beds || ''}
            onChange={handleNumberChange}
            min="0"
          />
        </div>
        <div className="col-md-4 col-6 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaBath /> Baths
          </label>
          <input 
            type="number" 
            className="form-control shadow-sm" 
            name="baths"
            value={formData.baths || ''}
            onChange={handleNumberChange}
            min="0"
          />
        </div>
        <div className="col-md-4 col-12 mb-3">
          <label className="form-label d-flex align-items-center gap-2">
            <FaMapMarkedAlt /> Map Link
          </label>
          <input 
            type="text" 
            className="form-control shadow-sm" 
            name="mapLink"
            value={formData.mapLink || ''}
            onChange={handleChange}
            placeholder="e.g. https://goo.gl/maps/..."
          />
        </div>
      </div>
      
      <div className="mb-3">
        <label className="form-label d-flex align-items-center gap-2">
          <FaInfoCircle /> Description
        </label>
        <textarea 
          className="form-control shadow-sm" 
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          rows="4"
          placeholder="Enter a detailed description of the property"
        ></textarea>
      </div>
    </motion.div>
  );
};

export default BasicInfoTab;
